import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { cn } from "../../utils/cn";
import { caseLabel, originLabel } from "./tickets";

// ─── Historial de casos de una conversación ──────────────────────────────────
// Popover, no side panel: `case_history` ya viene completo en getConversation,
// así que acá no hay ni un fetch — solo se pinta lo que la conversación trae.
//
// El caso VIGENTE no está en case_history (esa entrada se sella recién al
// resolver): se sintetiza arriba con el status actual. Las conversaciones
// legacy (sin case_number) son el caso 1 con historial vacío.
//
// GEMELO: STATUS_LABELS/STATUS_TONE duplican el mapa de ConversationDetail
// (solo las 3 entradas de triage). Si se toca uno, tocar el otro.
const STATUS_LABELS = {
  pending: "Pendiente",
  assigned: "Asignado",
  resolved: "Resuelto",
};

const STATUS_TONE = {
  pending: "bg-status-warning-soft text-status-warning",
  assigned: "bg-status-info-soft text-status-info",
  resolved: "bg-status-success-soft text-status-success",
};

function fmt(date) {
  return date ? dayjs(date).format("D MMM YYYY HH:mm") : "—";
}

export default function CaseHistoryPopover({ conversation, getDisplayName, onClose }) {
  const { t } = useTranslation();
  const rootRef = useRef(null);

  // Cierre por click-fuera y Escape (mismo patrón que el menú overflow del
  // detalle: el popover es hermano del botón que lo abre, no un modal).
  useEffect(() => {
    const handleClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      onClose?.();
    };
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  if (!conversation) return null;

  const currentNumber = conversation.case_number || 1;
  const history = Array.isArray(conversation.case_history)
    ? [...conversation.case_history].sort((a, b) => b.number - a.number)
    : [];

  const resolveName = (id) => {
    if (!id) return null;
    // Cierres automáticos y el cierre general los sella el backend como
    // 'system': no es un usuario que se pueda resolver.
    if (id === "system") return t("Sistema");
    const name = getDisplayName ? getDisplayName(id) : id;
    return name || id;
  };

  const currentAssigned = resolveName(conversation.assigned_to);
  // Con la conversación RESUELTA el caso vigente ya está sellado en el
  // historial (mismo number): se muestra esa entrada, que trae el cierre, en
  // vez de duplicarlo con la fila sintetizada.
  const currentSealed = history.some((entry) => entry.number === currentNumber);

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={t("Historial de casos")}
      className="absolute left-0 top-full mt-1 z-30 min-w-[240px] max-w-[300px] bg-bg border border-border rounded-md shadow-elevated animate-scale-in origin-top-left motion-reduce:animate-none"
    >
      <div className="px-3 pt-2 text-xs font-semibold text-fg">
        {t("Historial de casos")}
      </div>

      <div className="max-h-64 overflow-y-auto py-1.5">
        {/* Caso vigente (sintetizado: aún no está sellado en case_history) */}
        {!currentSealed && (
          <div className="px-3 py-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-semibold text-fg tabular-nums">
                {caseLabel(currentNumber, conversation.ticket_number, t)}
              </span>
              <span
                className={cn(
                  "inline-flex items-center px-2 h-[18px] rounded-full text-[11px] font-medium",
                  STATUS_TONE[conversation.status] ?? "bg-bg-muted text-fg-muted",
                )}
              >
                {t(STATUS_LABELS[conversation.status] ?? conversation.status)}
              </span>
            </div>
            <div className="mt-0.5 text-[11px] text-fg-muted">
              {t("Abierto")} {fmt(conversation.case_opened_at || conversation.createdAt || conversation.created_at)}
              {currentAssigned ? ` · ${currentAssigned}` : ""}
            </div>
          </div>
        )}

        {history.length === 0 ? (
          <div className="px-3 py-1.5 text-[11px] text-fg-muted">
            {t("Sin casos anteriores")}
          </div>
        ) : (
          history.map((entry, index) => {
            const attended = resolveName(entry.assigned_to);
            const closedBy = resolveName(entry.resolved_by);
            // "Cerró" solo aporta cuando NO fue el mismo que atendía (un
            // supervisor cerrando el caso de otro agente, p. ej.).
            const showClosedBy =
              closedBy && entry.resolved_by !== entry.assigned_to;
            // Cómo se cerró: solo los orígenes que el agente NO puede deducir
            // del resto de la fila (un cierre por API ya lo dice el "Cerró").
            const origin = originLabel(entry.origin, t);
            return (
              <div
                key={entry.number}
                className={cn(
                  "px-3 py-1.5",
                  // Sin fila sintetizada arriba, la primera no lleva divisor.
                  (index > 0 || !currentSealed) && "border-t border-border/40",
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold text-fg tabular-nums">
                    {caseLabel(entry.number, entry.ticket_number, t)}
                  </span>
                  <span className="inline-flex items-center px-2 h-[18px] rounded-full text-[11px] font-medium bg-status-success-soft text-status-success">
                    {t("Resuelto")}
                  </span>
                  {origin && (
                    <span className="inline-flex items-center px-2 h-[18px] rounded-full text-[11px] font-medium bg-bg-muted text-fg-muted">
                      {origin}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-[11px] text-fg-muted tabular-nums">
                  {fmt(entry.opened_at)} → {fmt(entry.resolved_at)}
                </div>
                <div className="text-[11px] text-fg-muted">
                  {t("Atendió")}: {attended || "—"}
                  {showClosedBy && ` · ${t("Cerró")}: ${closedBy}`}
                </div>
                {/* Nota del cierre (p. ej. el motivo de un cierre masivo). */}
                {entry.note && (
                  <p
                    className="mt-0.5 text-[11px] text-fg-muted line-clamp-3"
                    title={entry.note}
                  >
                    {entry.note}
                  </p>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
